/**
 * Opens pop-out windows. `window.open` with popup features opens a plain
 * window (no tabs or toolbar) in a browser, and an app window in the
 * installed Velora app (PWA, Chrome and Edge open in-scope popups as app
 * windows). The same module always reuses its one window (`windowName`).
 *
 * A browser that blocks the popup returns null: the window is listed in the
 * blocked-popups store and PopoutBlockedHint offers it again on a click.
 */
import { useSyncExternalStore } from 'react';
import {
  DEFAULT_SIZE,
  popoutHref,
  popoutSpec,
  windowFeatures,
  windowName,
  type PopoutSpec,
} from './popout.js';
import { readGeometry } from './workspace.js';

export interface PopoutRequest {
  key: string;
  label: string;
  /** Path plus query of the page to show (without `pop`). */
  href: string;
  office?: string | null;
  pinned?: boolean;
}

function features(spec: PopoutSpec | undefined, key: string): string {
  const s = window.screen as Screen & { availLeft?: number; availTop?: number };
  return windowFeatures(readGeometry(key), spec ?? DEFAULT_SIZE, {
    availLeft: s.availLeft ?? 0,
    availTop: s.availTop ?? 0,
    availWidth: s.availWidth || window.outerWidth,
    availHeight: s.availHeight || window.outerHeight,
    openerLeft: window.screenX,
    openerTop: window.screenY,
    openerWidth: window.outerWidth,
  });
}

/** Opens (or focuses and re-points) the module's pop-out; false when the browser blocked it. */
export function openPopout(req: PopoutRequest): boolean {
  const url = popoutHref(req.href, {
    key: req.key,
    ...(req.office ? { office: req.office } : {}),
    ...(req.pinned ? { pinned: true } : {}),
  });
  let win: Window | null = null;
  try {
    win = window.open(url, windowName(req.key), features(popoutSpec(req.key), req.key));
  } catch {
    win = null;
  }
  if (!win || win.closed) {
    addBlocked(req);
    return false;
  }
  removeBlocked(req.key);
  try {
    win.focus();
  } catch {
    // Some browsers refuse focus from script; the window is open anyway.
  }
  return true;
}

/* ---------- blocked pop-ups (PopoutBlockedHint) ---------- */

let blocked: readonly PopoutRequest[] = [];
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

function addBlocked(req: PopoutRequest) {
  blocked = [...blocked.filter((b) => b.key !== req.key), req];
  emit();
}

export function removeBlocked(key: string): void {
  if (!blocked.some((b) => b.key === key)) return;
  blocked = blocked.filter((b) => b.key !== key);
  emit();
}

export function clearBlocked(): void {
  if (blocked.length === 0) return;
  blocked = [];
  emit();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const get = () => blocked;

export function useBlockedPopouts(): readonly PopoutRequest[] {
  return useSyncExternalStore(subscribe, get, get);
}
